/**
 * Settings store: the one piece of persistent client state in the product.
 *
 * The codebase has no state library, no Context, and no localStorage usage
 * anywhere. This follows the existing shape: a hook holding useState,
 * persisting to localStorage under csh_settings_v1, matching the
 * csh_sessions_v1 key convention written down in NEXT_PROMPT.md.
 *
 * Reads are defensive. A stored blob that fails to parse, or carries an
 * unknown version, falls back to defaults rather than throwing. Export and
 * import are a Blob download and a file input reading the same JSON shape,
 * so a settings file is portable across machines.
 */

import { useCallback, useState } from 'react';

import { createRandom } from '@harness/protocol';

const STORAGE_KEY = 'csh_settings_v1';

export interface Knob {
  key: string;
  label: string;
  kind: 'int' | 'bool' | 'enum' | 'ms' | 'rate';
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  value: number | boolean | string;
  options?: string[];
}

export interface HarnessSettings {
  version: 1;
  /** Per scenario knob values, keyed by scenario id then knob key. */
  overridesByScenario: Record<string, Record<string, number | boolean | string>>;
  /** Seeded random values within each knob's declared range. */
  randomizer: { enabled: boolean; seed: number; keys: string[] };
  /** Seeded random fault injection into whichever scenario is selected. */
  chaos: { enabled: boolean; intensity: number; faults: string[]; seed: number };
  /** Cycles the whole catalogue forever, counting pass and fail. */
  endless: { enabled: boolean; delayMs: number };
  /** Client resilience injected into the stream machine. */
  client: {
    maxReconnectAttempts: number;
    byteTimeoutMs: number;
    contentTimeoutMs: number;
    seededJitter: boolean;
  };
}

export const DEFAULT_SETTINGS: HarnessSettings = {
  version: 1,
  overridesByScenario: {},
  randomizer: { enabled: false, seed: 0, keys: [] },
  chaos: { enabled: false, intensity: 0.3, faults: [], seed: 0 },
  endless: { enabled: false, delayMs: 2000 },
  client: {
    maxReconnectAttempts: 5,
    byteTimeoutMs: 30_000,
    contentTimeoutMs: 20_000,
    seededJitter: false,
  },
};

/** A deep copy of the defaults, so callers can mutate without aliasing. */
function defaults(): HarnessSettings {
  return structuredClone(DEFAULT_SETTINGS);
}

/**
 * Read settings from localStorage. Falls back to defaults on any failure:
 * a missing key, a parse error, or an unknown version. Never throws.
 */
function readSettings(): HarnessSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return defaults();
    const parsed = JSON.parse(raw) as Partial<HarnessSettings>;
    if (parsed.version !== 1) return defaults();

    return {
      version: 1,
      overridesByScenario: parsed.overridesByScenario ?? {},
      randomizer: { ...defaults().randomizer, ...parsed.randomizer },
      chaos: { ...defaults().chaos, ...parsed.chaos },
      endless: { ...defaults().endless, ...parsed.endless },
      client: { ...defaults().client, ...parsed.client },
    };
  } catch {
    return defaults();
  }
}

function writeSettings(settings: HarnessSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Quota exceeded or storage disabled. Settings are session only in that
    // case, which is better than crashing.
  }
}

export interface SettingsController {
  settings: HarnessSettings;
  update: (updater: (prev: HarnessSettings) => HarnessSettings) => void;
  reset: () => void;
  exportConfig: () => void;
  importConfig: (file: File) => Promise<void>;
}

export function useSettings(): SettingsController {
  const [settings, setSettings] = useState<HarnessSettings>(readSettings);

  const update = useCallback((updater: (prev: HarnessSettings) => HarnessSettings) => {
    setSettings((prev) => {
      const next = updater(prev);
      writeSettings(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    const fresh = defaults();
    writeSettings(fresh);
    setSettings(fresh);
  }, []);

  const exportConfig = useCallback(() => {
    const blob = new Blob([JSON.stringify(settings, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'csh-settings.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [settings]);

  const importConfig = useCallback(async (file: File) => {
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as Partial<HarnessSettings>;
      if (parsed.version !== 1) return;
      const imported: HarnessSettings = {
        version: 1,
        overridesByScenario: parsed.overridesByScenario ?? {},
        randomizer: { ...defaults().randomizer, ...parsed.randomizer },
        chaos: { ...defaults().chaos, ...parsed.chaos },
        endless: { ...defaults().endless, ...parsed.endless },
        client: { ...defaults().client, ...parsed.client },
      };
      writeSettings(imported);
      setSettings(imported);
    } catch {
      // A corrupt file is ignored rather than crashing the page.
    }
  }, []);

  return { settings, update, reset, exportConfig, importConfig };
}

/**
 * Draw random values for the given knobs from their declared ranges, using the
 * seeded generator so the same seed reproduces the same run. Returns a values
 * map suitable for the overrides field in the request body.
 */
export function drawRandomValues(
  knobs: Knob[],
  seed: number,
): Record<string, number | boolean | string> {
  const random = createRandom(seed);
  const out: Record<string, number | boolean | string> = {};

  for (const knob of knobs) {
    if (knob.kind === 'bool') {
      out[knob.key] = random() < 0.5;
    } else if (knob.kind === 'enum') {
      // Enum knobs are not randomized; keep the default.
      out[knob.key] = knob.value;
    } else {
      // The only kinds left once bool and enum are ruled out: int, ms, rate.
      const min = knob.min ?? 0;
      const max = knob.max ?? min + 1;
      const step = knob.step ?? 1;
      const range = max - min;
      const raw = min + random() * range;
      const stepped = Math.round(raw / step) * step;
      out[knob.key] = Math.round(stepped);
    }
  }

  return out;
}

/**
 * Build the overrides object for a request, combining manual knob values with
 * randomizer mode. When randomizer is on, values are drawn from the knob
 * ranges using the seed; otherwise the stored manual values are used.
 */
export function buildOverrides(
  settings: HarnessSettings,
  scenarioId: string,
  knobs: Knob[],
):
  | {
      values: Record<string, number | boolean | string>;
      chaos?: { intensity: number; faults: string[]; seed: number };
    }
  | undefined {
  const hasKnobs = knobs.length > 0;
  const hasChaos = settings.chaos.enabled && settings.chaos.faults.length > 0;

  if (!hasKnobs && !hasChaos) return undefined;

  const values: Record<string, number | boolean | string> = {};

  if (hasKnobs) {
    if (settings.randomizer.enabled) {
      const random = drawRandomValues(knobs, settings.randomizer.seed);
      for (const key of Object.keys(random)) {
        const val = random[key];
        if (val !== undefined) values[key] = val;
      }
    } else {
      const stored = settings.overridesByScenario[scenarioId];
      if (stored !== undefined) {
        for (const key of Object.keys(stored)) {
          const val = stored[key];
          if (val !== undefined) values[key] = val;
        }
      }
    }
  }

  const result: {
    values: Record<string, number | boolean | string>;
    chaos?: { intensity: number; faults: string[]; seed: number };
  } = { values };

  if (hasChaos) {
    result.chaos = {
      intensity: settings.chaos.intensity,
      faults: settings.chaos.faults,
      seed: settings.chaos.seed,
    };
  }

  return result;
}
